import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUpdateCandidate } from '@/hooks/use-candidates';
import { extractErrorMessage } from '@/lib/errors';
import type { ParseConflict } from '@/types/candidates';
import { isAxiosError } from 'axios';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';

interface ContactConflictModalProps {
  conflict: ParseConflict | null;
  onClose: () => void;
  // Edit-side path: PATCH the existing candidate with the parsed value.
  candidateId?: string;
  // Create-side path: candidate doesn't exist yet — caller swaps local state.
  onApply?: (value: string) => void;
}

interface BlockingDup {
  existingCandidateId: string;
}

const FIELD_LABEL: Record<ParseConflict['field'], string> = {
  email: 'Email',
  phone: 'Phone',
};

export function ContactConflictModal({
  candidateId,
  conflict,
  onClose,
  onApply,
}: ContactConflictModalProps) {
  // Hook must run unconditionally; an empty id is safe because the mutation
  // only fires in the edit-side branch of handleReplace.
  const update = useUpdateCandidate(candidateId ?? '');
  const open = Boolean(conflict);
  const fieldLabel = conflict ? FIELD_LABEL[conflict.field] : 'Email';
  // HW-528: an email conflict whose value is already owned by another candidate is
  // surfaced from the start so the user can jump to the other profile right away.
  // HW-516: Re-check stays available so the user can retry after freeing the other email.
  const preBlockedId = conflict?.blockedByCandidateId ?? null;
  const [blockingDup, setBlockingDup] = useState<BlockingDup | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setBlockingDup(null);
      setGenericError(null);
      return;
    }
    if (preBlockedId) {
      setBlockingDup({ existingCandidateId: preBlockedId });
    } else {
      setBlockingDup(null);
    }
    setGenericError(null);
  }, [open, preBlockedId]);

  const handleReplace = async () => {
    if (!conflict) return;
    setBlockingDup(null);
    setGenericError(null);

    if (onApply) {
      onApply(conflict.parsedValue);
      onClose();
      return;
    }

    try {
      const patch =
        conflict.field === 'email'
          ? { email: conflict.parsedValue }
          : { phone: conflict.parsedValue };
      await update.mutateAsync(patch);
      onClose();
    } catch (err) {
      // The 409 duplicate response only applies to email — phone has no unique constraint.
      if (conflict.field === 'email' && isAxiosError(err) && err.response?.status === 409) {
        const body = err.response.data as { code?: string; existingCandidateId?: string };
        if (body.code === 'CANDIDATE_EMAIL_DUPLICATE' && body.existingCandidateId) {
          setBlockingDup({ existingCandidateId: body.existingCandidateId });
          return;
        }
      }
      setGenericError(extractErrorMessage(err, `Failed to replace ${fieldLabel.toLowerCase()}`));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose())}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {fieldLabel} conflict
          </DialogTitle>
          <DialogDescription>
            {preBlockedId
              ? 'The email parsed from the CV is already used by another candidate, so it cannot be added to this profile.'
              : `The CV contains a different ${fieldLabel.toLowerCase()} than the current one.`}
          </DialogDescription>
        </DialogHeader>

        {conflict && (
          <div className="space-y-3 text-sm">
            {/* HW-528: the blocked-email case has an empty existing value — only render the
                "current" slot when there's actually something on the profile to compare. */}
            {conflict.existingValue && (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
                <div className="text-xs text-muted-foreground">Current value</div>
                <div className="wrap-anywhere font-medium">{conflict.existingValue}</div>
              </div>
            )}
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
              <div className="text-xs text-muted-foreground">Found in CV</div>
              <div className="wrap-anywhere font-medium">{conflict.parsedValue}</div>
            </div>
          </div>
        )}

        {blockingDup && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="space-y-0.5">
                  <p className="wrap-anywhere font-medium">
                    {conflict?.blockedByCandidateName
                      ? `Email already used by ${conflict.blockedByCandidateName}.`
                      : 'Email already used by another candidate.'}
                  </p>
                  <p className="text-xs">
                    Open that profile to clear or change its email, then Re-check — or Keep current to discard the CV's email.
                  </p>
                </div>
                <a
                  href={`/recruitment/candidates/${blockingDup.existingCandidateId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 bg-background/80 px-2.5 py-1 text-xs font-medium text-foreground hover:bg-background"
                >
                  Open existing candidate
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        )}

        {genericError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {genericError}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            {preBlockedId ? 'Close' : 'Keep current'}
          </Button>
          <Button variant="accent" onClick={handleReplace} disabled={update.isPending}>
            {blockingDup
              ? update.isPending
                ? 'Re-checking…'
                : 'Re-check'
              : update.isPending
                ? 'Updating…'
                : 'Replace with CV'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}